import styled from "@emotion/styled";
import type { CSSProperties, MouseEvent } from "react";

import {
  Icon,
  SkiliticsDuotoneIcon,
  SkiliticsIcon,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

const ButtonWrapper = styled.span``;

const Divider = styled.div`
  width: 1px;
  height: 20px;
  background-color: ${({ theme }) => theme.outlineSoft};
  flex: 0 0 auto;
`;

const StyledToggleButton = styled(ToggleButton)`
  border: 0;
  border-radius: ${({ theme }) => theme.borderRadius}px !important;
  height: 24px;
  width: 24px;
  font-size: 15px;
  padding: 0;
  color: ${({ theme }) => theme.generalTextIcon};
  &[disabled] {
    border: 0;
  }
`;

const StyledToggleButtonGroup = styled(ToggleButtonGroup)`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const OverflowPickerContainer = styled.div`
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 22px;
`;

type Overflow = CSSProperties["overflowX"] | CSSProperties["overflowY"];

type Props = {
  overflowX: Overflow;
  overflowY: Overflow;
  onOverflowChange: ({ overflowX, overflowY }: { overflowX: Overflow; overflowY: Overflow }) => void;
};

export const OverflowPicker = ({ overflowX, overflowY, onOverflowChange }: Props) => {
  const formatOverflowMessage = useFormatMessage(overflowFormatStrings);

  const handleOverflowXChange = (_: MouseEvent<HTMLElement>, value: Overflow) => {
    if (value && value !== overflowX) {
      // Per CSS rules: if you are using visible for either overflow-x or overflow-y
      // and something other than visible for the other, the visible value is interpreted as auto.
      // https://stackoverflow.com/questions/6421966/css-overflow-x-visible-and-overflow-y-hidden-causing-scrollbar-issue
      let overflowYValue = overflowY;
      if (overflowYValue === "auto" && value === "visible") {
        overflowYValue = "visible";
      } else if (overflowYValue === "visible" && value === "auto") {
        overflowYValue = "clip";
      }
      onOverflowChange({ overflowX: value, overflowY: overflowYValue });
    }
  };

  const handleOverflowYChange = (_: MouseEvent<HTMLElement>, value: Overflow) => {
    if (value && value !== overflowY) {
      let overflowXValue = overflowX;
      if (overflowXValue === "auto" && value === "visible") {
        overflowXValue = "visible";
      } else if (overflowXValue === "visible" && value === "auto") {
        overflowXValue = "clip";
      }
      onOverflowChange({ overflowX: overflowXValue, overflowY: value });
    }
  };

  const overflowYVisibleDisabled = overflowX === "auto";
  const overflowXVisibleDisabled = overflowY === "auto";

  return (
    <OverflowPickerContainer>
      <StyledToggleButtonGroup onChange={handleOverflowXChange} value={overflowX} exclusive color="primary">
        <Tooltip title={formatOverflowMessage("autoX")} placement="top">
          <StyledToggleButton value="auto">
            <SkiliticsIcon icon="horizontal-scroll" size={18} />
          </StyledToggleButton>
        </Tooltip>
        <Tooltip title={formatOverflowMessage("hiddenX")} placement="top">
          <StyledToggleButton value="clip">
            <Icon icon="scissors" />
          </StyledToggleButton>
        </Tooltip>
        <Tooltip
          title={formatOverflowMessage(overflowXVisibleDisabled ? "visibleDisabled" : "visibleX")}
          placement="top"
        >
          <ButtonWrapper>
            <StyledToggleButton value="visible">
              <SkiliticsDuotoneIcon icon="overflow-visible-horizontal" size={18} />
            </StyledToggleButton>
          </ButtonWrapper>
        </Tooltip>
      </StyledToggleButtonGroup>

      <Divider />

      <StyledToggleButtonGroup onChange={handleOverflowYChange} value={overflowY} exclusive color="primary">
        <Tooltip title={formatOverflowMessage("autoY")} placement="top">
          <StyledToggleButton value="auto">
            <SkiliticsIcon icon="vertical-scroll" size={18} />
          </StyledToggleButton>
        </Tooltip>
        <Tooltip title={formatOverflowMessage("hiddenY")} placement="top">
          <StyledToggleButton value="clip">
            <Icon icon="scissors" />
          </StyledToggleButton>
        </Tooltip>
        <Tooltip
          title={formatOverflowMessage(overflowYVisibleDisabled ? "visibleDisabled" : "visibleY")}
          placement="top"
        >
          <ButtonWrapper>
            <StyledToggleButton value="visible">
              <SkiliticsDuotoneIcon icon="overflow-visible-vertical" size={18} />
            </StyledToggleButton>
          </ButtonWrapper>
        </Tooltip>
      </StyledToggleButtonGroup>
    </OverflowPickerContainer>
  );
};

const overflowFormatStrings = {
  hiddenX: {
    en: "Clip content that overflows horizontally",
  },
  autoX: {
    en: "Show a scrollbar at bottom of container when content overflows horizontally",
  },
  visibleX: {
    en: "Content can overflow the horizontal bounds of the container",
  },
  hiddenY: {
    en: "Clip content that overflows vertically",
  },
  autoY: {
    en: "Show a scrollbar at bottom of container when content overflows vertically",
  },
  visibleY: {
    en: "Content can overflow the vertical bounds of the container",
  },
  visibleDisabled: {
    en: "When overflow behaviour is set to 'visible', the corresponding axis must also be set to 'visible' or 'clip'.",
  },
};
